import { getToken, onMessage } from "firebase/messaging";
import { messaging, firebaseConfig, hasFirebaseConfig } from "../firebase/firebaseConfig";
import { toasterError, toasterSuccess, toasterWarning } from "../Components/toaster";
import { registerFcmToken, getNotifications } from "../ApiHelper/url_Helpers";
import notificationsService from "./notificationsService";

const VAPID_KEY =
  "BI_1dTRCi_kGCvFuRCxzBkg5aZoJYGS0WZWyU3tcdMqzLDsaFqu1T09VE69ZJ3_LYVD0Ejrym7h417GPp6U6KaE";

export const requestNotificationPermission = async () => {
  if (typeof Notification === "undefined") {
    throw new Error("This browser does not support the Notification API.");
  }

  const permission = await Notification.requestPermission();

  if (permission === "denied") {
    throw new Error("Notification permission was denied. Enable browser notifications to receive alerts.");
  }

  if (permission !== "granted") {
    throw new Error("Notification permission was not granted.");
  }

  return permission;
};

export const registerFirebaseMessagingSW = async () => {
  if (!("serviceWorker" in navigator)) {
    throw new Error("Service workers are not supported by this browser. FCM background notifications require HTTPS and a compatible browser.");
  }

  // Service worker must live in public/ and be registered from the root scope.
  return navigator.serviceWorker.register(`${process.env.PUBLIC_URL}/firebase-messaging-sw.js`);
};

export const getFcmToken = async () => {

  if (!hasFirebaseConfig) {
    throw new Error(
      "Firebase is not configured. Set REACT_APP_FIREBASE_API_KEY in .env and restart the app."
    );
  }

  if (!("Notification" in window) || Notification.permission !== "granted") {
    throw new Error("Browser notification permission is not granted.");
  }

  const serviceWorkerRegistration = await navigator.serviceWorker.ready;

  try {
    const currentToken = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration,
    });

    if (!currentToken) {
      throw new Error("Unable to obtain FCM registration token. Please retry or re-enable notifications.");
    }

    return currentToken;
  } catch (error) {
    console.error("Failed to retrieve FCM token", error);
    throw new Error(
      "Failed to obtain FCM token. Check that the Firebase API key is valid, the web app config is correct, and the service worker is using the matching Firebase project settings."
    );
  }
};

export const saveFcmTokenToServer = async (token) => {
  try {
    if (!token) {
      throw new Error("Invalid FCM token.");
    }

   const result = await registerFcmToken({ token, platform: "web" });
   // toasterSuccess("Push notifications enabled.");
   // fetch existing notifications from backend and persist locally
   if (result?.message) {
     const resp = await getNotifications();
      const list = resp?.notifications || [];
      const unreadCount = resp?.unread_count ?? 0;
      // store unread count from api
      notificationsService.setUnreadCount(unreadCount);
    } 
  } catch (error) {
    // toasterError("Could not save device token. Push notifications may not work.");
    console.error("FCM token save error:", error);
    throw error;
  }
};

export const listenForForegroundMessages = (callback) => {
  if (!hasFirebaseConfig || !messaging) {
    return () => {};
  }

  return onMessage(messaging, (payload) => {
    console.log("Foreground FCM message received:", payload);

    const title = payload?.notification?.title || "New notification";
    const body = payload?.notification?.body || payload?.data?.body || "You have a new message.";
    const icon = payload?.notification?.icon || "/favicon.ico";

    if (Notification.permission === "granted") {
      try {
        new Notification(title, { body, icon });
      } catch (err) {
        console.warn("Browser Notification API failed:", err);
      }
    }

    // toasterSuccess(body);

    // persist so UI off-canvas can show it
    try {
      notificationsService.addNotification(payload);
    } catch (e) {
      console.warn("Failed to persist foreground notification", e);
    }

    if (typeof callback === "function") {
      callback(payload);
    }
  });
};

export const refreshFcmToken = async () => {
  const token = await getFcmToken();
  await saveFcmTokenToServer(token);
  return token;
};
